package br.gov.go.pge.permanenciaweb.site.pages.admin

import androidx.compose.runtime.*
import br.gov.go.pge.permanenciaweb.shared.AccessGrant
import br.gov.go.pge.permanenciaweb.shared.AccessPreset
import br.gov.go.pge.permanenciaweb.shared.ActivateUserRequest
import br.gov.go.pge.permanenciaweb.shared.AdminUserDto
import br.gov.go.pge.permanenciaweb.shared.AdminUsersDto
import br.gov.go.pge.permanenciaweb.shared.Capability
import br.gov.go.pge.permanenciaweb.shared.ErrorDto
import br.gov.go.pge.permanenciaweb.shared.GrantDto
import br.gov.go.pge.permanenciaweb.shared.GrantHistoryDto
import br.gov.go.pge.permanenciaweb.shared.LeadershipRunDto
import br.gov.go.pge.permanenciaweb.shared.MeDto
import br.gov.go.pge.permanenciaweb.shared.UpdateAccessRequest
import br.gov.go.pge.permanenciaweb.shared.grants
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import br.gov.go.pge.permanenciaweb.site.components.*
import com.varabyte.kobweb.browser.http.bodyAsBytes
import com.varabyte.kobweb.browser.http.bodyOf
import com.varabyte.kobweb.browser.http.http
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.css.autoLength
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.forms.Checkbox
import com.varabyte.kobweb.silk.components.forms.CheckboxSize
import com.varabyte.kobweb.silk.components.icons.ChevronDownIcon
import com.varabyte.kobweb.silk.components.icons.ChevronUpIcon
import com.varabyte.kobweb.silk.style.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement

/*
 * Access management page. The page answers two separate questions per account:
 *   - status: can this person log in? (Pendente / Ativa / Negada)
 *   - permissions: what can the account do? (grant rows)
 *
 * Presets (Usuário/Chefia/Administrador) are shortcuts, not stored state; the active table shows
 * the effective permissions, never a "level". Sync-managed grants are read-only in the editor.
 */

private val json = Json { ignoreUnknownKeys = true; encodeDefaults = true }

val AdminMainStyle = CssStyle.base {
    Modifier
        .maxWidth(72.cssRem)
        .padding(top = 1.cssRem, right = 1.5.cssRem, bottom = 1.5.cssRem, left = 1.5.cssRem)
        .display(DisplayStyle.Flex)
        .flexDirection(FlexDirection.Column)
        .margin { leftRight(autoLength) }
        .gap(1.cssRem)
}

val AdminHintStyle = CssStyle.base {
    Modifier.color(IndustrialPalette.Muted).margin(0.cssRem)
}

/** Display name beside the username, truncated with an ellipsis so long names can't widen the table. */
val AdminNameHintStyle = CssStyle.base {
    Modifier
        .color(IndustrialPalette.Muted)
        .fontSize(0.75.cssRem)
        .maxWidth(14.cssRem)
        .overflow(Overflow.Hidden)
        .textOverflow(TextOverflow.Ellipsis)
        .whiteSpace(WhiteSpace.NoWrap)
}

sealed interface PresetSelectKind : ComponentKind

val PresetSelectStyle = CssStyle<PresetSelectKind> {
    base {
        Modifier
            .color(IndustrialPalette.Ink)
            .fontSize(0.8125.cssRem)
            .backgroundColor(IndustrialPalette.White)
            .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
            .borderRadius(0.25.cssRem)
            .padding(0.1875.cssRem, 0.375.cssRem)
    }
}

val AdminSearchStyle = CssStyle.base {
    Modifier
        .maxWidth(20.cssRem)
        .color(IndustrialPalette.Ink)
        .fontSize(0.8125.cssRem)
        .backgroundColor(IndustrialPalette.White)
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.25.cssRem)
        .padding(0.375.cssRem, 0.625.cssRem)
}

val WarningBannerStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .backgroundColor(IndustrialPalette.OrangeTint)
        .border(1.px, LineStyle.Solid, IndustrialPalette.Orange)
        .borderRadius(0.25.cssRem)
        .padding(0.5.cssRem, 0.75.cssRem)
        .fontSize(0.8125.cssRem)
}

sealed interface AccessChipKind : ComponentKind

val AccessChipStyle = CssStyle<AccessChipKind> {
    base {
        Modifier
            .padding(0.125.cssRem, 0.4375.cssRem)
            .borderRadius(0.625.cssRem)
            .fontSize(0.6875.cssRem)
            .backgroundColor(IndustrialPalette.SteelTint)
            .color(IndustrialPalette.Steel)
    }
}

val AccessChipDangerVariant = AccessChipStyle.addVariantBase {
    Modifier.backgroundColor(IndustrialPalette.RedTint).color(IndustrialPalette.Red)
}

val AccessChipWarningVariant = AccessChipStyle.addVariantBase {
    Modifier.backgroundColor(IndustrialPalette.OrangeTint).color(IndustrialPalette.Orange)
}

val AccessChipMutedVariant = AccessChipStyle.addVariantBase {
    Modifier.backgroundColor(IndustrialPalette.Surface).color(IndustrialPalette.Muted)
}

val SyncWarningStyle = CssStyle.base {
    Modifier.color(IndustrialPalette.Orange).fontSize(0.8125.cssRem).margin(0.cssRem)
}

internal class AdminUsersState {
    var loaded by mutableStateOf(false)
    var error by mutableStateOf("")

    var search by mutableStateOf("")

    var pending by mutableStateOf<List<AdminUserDto>>(emptyList())
    var active by mutableStateOf<List<AdminUserDto>>(emptyList())
    var denied by mutableStateOf<List<AdminUserDto>>(emptyList())

    var filter by mutableStateOf(UserFilter.ALL)

    var activeExpanded by mutableStateOf(true)
    var deniedExpanded by mutableStateOf(false)

    var me by mutableStateOf<MeDto?>(null)
    var currentUsername by mutableStateOf("")

    var editor by mutableStateOf<AccessEditorState?>(null)

    /** Pending-table deny waiting for the accessible confirmation dialog. */
    var denyTarget by mutableStateOf<AdminUserDto?>(null)

    var syncRun by mutableStateOf<LeadershipRunDto?>(null)
    var syncLoaded by mutableStateOf(false)
    var syncError by mutableStateOf("")
    var syncExpanded by mutableStateOf(false)

    /** Per-row draft in the pending table. */
    var pendingPreset by mutableStateOf<Map<String, AccessPreset>>(emptyMap())
    var pendingReport by mutableStateOf<Map<String, Boolean>>(emptyMap())

    var toastMessage by mutableStateOf("")
    var toastVisible by mutableStateOf(false)
    var toastSeq by mutableStateOf(0)

    val searchActive: Boolean get() = search.isNotBlank()

    /** Active accounts that cannot open the panel (includes accounts with no permissions). */
    val withoutReportCount: Int get() = active.count { !it.grants.has(Capability.VIEW_REPORT) }

    val activeFiltered: List<AdminUserDto>
        get() = active.filter { filter.matches(it) && matchesSearch(it) }

    val activeVisible: Boolean get() = activeExpanded || searchActive
    val deniedVisible: Boolean get() = deniedExpanded || searchActive

    fun matchesSearch(user: AdminUserDto): Boolean {
        val query = search.trim()
        if (query.isEmpty()) return true
        return user.username.contains(query, ignoreCase = true) ||
            user.displayName?.contains(query, ignoreCase = true) == true
    }

    fun filtered(users: List<AdminUserDto>): List<AdminUserDto> = users.filter(::matchesSearch)

    fun filterCount(filter: UserFilter): Int = active.count { filter.matches(it) }

    fun presetFor(user: AdminUserDto): AccessPreset =
        pendingPreset[user.username] ?: AccessPreset.USER

    fun setPreset(username: String, preset: AccessPreset) {
        pendingPreset = pendingPreset + (username to preset)
        if (preset == AccessPreset.ADMIN) pendingReport = pendingReport + (username to true)
    }

    fun reportFor(user: AdminUserDto): Boolean =
        pendingReport[user.username] ?: false

    fun setReport(username: String, report: Boolean) {
        pendingReport = pendingReport + (username to report)
    }

    /** The inline pending controls prefill the editor when the admin chooses "Revisar". */
    fun pendingDraft(user: AdminUserDto): Set<AccessGrant> {
        val preset = presetFor(user)
        val report = preset == AccessPreset.ADMIN || reportFor(user)
        return preset.grants + if (report) setOf(AccessGrant(Capability.VIEW_REPORT)) else emptySet()
    }

    fun toast(message: String) {
        toastMessage = message
        toastSeq++
    }
}

@Page
@Composable
fun Usuarios() {
    val state = remember { AdminUsersState() }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        document.title = "Usuários — Permanência no Prédio"
        if (AppGlobals.isExporting) return@LaunchedEffect
        load(state)
    }

    val toastSeq = state.toastSeq
    LaunchedEffect(toastSeq) {
        if (toastSeq == 0) return@LaunchedEffect
        state.toastVisible = true
        delay(4000)
        state.toastVisible = false
    }

    AdminUsersPage(state) { action -> scope.launch { action() } }
}

@Composable
private fun AdminUsersPage(state: AdminUsersState, launchAction: ((suspend () -> Unit) -> Unit)) {
    val pending = state.filtered(state.pending)
    val denied = state.filtered(state.denied)
    val active = state.activeFiltered

    Main(attrs = AdminMainStyle.toAttrs()) {
        Row(
            modifier = Modifier.width(100.percent).gap(1.cssRem),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            H1(attrs = Modifier.fontSize(1.25.cssRem).fontWeight(FontWeight.SemiBold).margin(0.cssRem).toAttrs()) {
                Text("Usuários")
            }
            P(attrs = AdminHintStyle.toAttrs()) {
                Text("Aprovação de contas e permissões de acesso.")
            }
            Spacer()
            UserMenu(state.me, currentPath = "/admin/usuarios")
        }

        if (state.withoutReportCount > 0) {
            Row(
                modifier = WarningBannerStyle.toModifier().gap(0.75.cssRem),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("${state.withoutReportCount} conta(s) ativa(s) sem acesso ao painel.")
                Spacer()
                Button(
                    onClick = { state.filter = UserFilter.NO_REPORT },
                    variant = LinkButtonVariant,
                    size = ButtonSize.SM,
                ) { Text("Ver contas") }
            }
        }

        Input(
            type = InputType.Text,
            attrs = AdminSearchStyle.toAttrs {
                attr("id", "admin-user-search")
                attr("aria-label", "Buscar usuário")
                placeholder("Buscar usuário")
                onInput { event ->
                    state.search = (event.target as? HTMLInputElement)?.value.orEmpty()
                }
            },
        )

        Row(modifier = Modifier.gap(0.375.cssRem)) {
            for (filter in UserFilter.entries) {
                Button(
                    onClick = { state.filter = filter },
                    variant = if (state.filter == filter) PrimaryButtonVariant else DefaultButtonVariant,
                    size = ButtonSize.SM,
                ) {
                    Text("${filter.label} ${state.filterCount(filter)}")
                }
            }
        }

        if (state.error.isNotEmpty()) {
            P(attrs = Modifier.color(IndustrialPalette.Red).margin(0.cssRem).toAttrs()) {
                Text(state.error)
            }
        }

        Panel {
            PanelTitle { Text(sectionCount("Aguardando aprovação", pending.size, state.pending.size)) }
            when {
                !state.loaded -> P(attrs = PanelEmptyStyle.toAttrs()) { Text("Carregando…") }
                pending.isEmpty() -> P(attrs = PanelEmptyStyle.toAttrs()) {
                    Text(
                        if (state.searchActive) "Nenhum usuário encontrado para a busca."
                        else "Nenhum usuário aguardando aprovação."
                    )
                }
                else -> PendingTable(pending, state, launchAction)
            }
        }

        Panel {
            CollapsiblePanelTitle(
                expanded = state.activeVisible,
                onToggle = { state.activeExpanded = !state.activeExpanded },
            ) {
                Text(sectionCount("Ativos", active.size, state.active.size))
                if (state.activeVisible) {
                    ChevronUpIcon(Modifier.margin(left = 0.375.cssRem))
                } else {
                    ChevronDownIcon(Modifier.margin(left = 0.375.cssRem))
                }
            }
            if (state.activeVisible) {
                when {
                    !state.loaded -> P(attrs = PanelEmptyStyle.toAttrs()) { Text("Carregando…") }
                    active.isEmpty() -> P(attrs = PanelEmptyStyle.toAttrs()) {
                        Text(
                            when {
                                state.searchActive -> "Nenhum usuário encontrado para a busca."
                                state.filter != UserFilter.ALL -> "Nenhum usuário com esse filtro."
                                else -> "Nenhum usuário ativo."
                            }
                        )
                    }
                    else -> ActiveTable(active, state, launchAction)
                }
            }
        }

        Panel {
            CollapsiblePanelTitle(
                expanded = state.deniedVisible,
                onToggle = { state.deniedExpanded = !state.deniedExpanded },
            ) {
                Text(sectionCount("Negados", denied.size, state.denied.size))
                if (state.deniedVisible) {
                    ChevronUpIcon(Modifier.margin(left = 0.375.cssRem))
                } else {
                    ChevronDownIcon(Modifier.margin(left = 0.375.cssRem))
                }
            }
            if (state.deniedVisible) {
                when {
                    !state.loaded -> P(attrs = PanelEmptyStyle.toAttrs()) { Text("Carregando…") }
                    denied.isEmpty() -> P(attrs = PanelEmptyStyle.toAttrs()) {
                        Text(
                            if (state.searchActive) "Nenhum usuário encontrado para a busca."
                            else "Nenhum usuário negado."
                        )
                    }
                    else -> DeniedTable(denied, state, launchAction)
                }
            }
        }

        SyncPanel(state, launchAction)

        Toast(state.toastMessage, state.toastVisible)

        state.editor?.let { editor ->
            AccessEditorModal(
                state = editor,
                onClose = { if (!editor.saving) state.editor = null },
                onSave = { launchAction { state.saveEditor() } },
                onDeny = {
                    launchAction {
                        if (state.deny(editor.user.username)) state.editor = null
                    }
                },
            )
        }

        state.denyTarget?.let { target ->
            val copy = denyAccessConfirmation(
                username = target.username,
                isSelf = target.username == state.currentUsername,
            )
            ConfirmDialog(
                title = copy.title,
                message = copy.message,
                confirmLabel = copy.confirmLabel,
                selfWarning = copy.selfWarning,
                dialogId = "admin-deny-confirm",
                onCancel = { state.denyTarget = null },
                onConfirm = {
                    state.denyTarget = null
                    launchAction { state.deny(target.username) }
                },
            )
        }
    }
}

/** "Base — 3" or "Base — 1 de 3" while the search/filter hides rows. */
private fun sectionCount(base: String, shown: Int, total: Int): String =
    if (shown != total) "$base — $shown de $total" else "$base — $total"

@Composable
private fun PendingTable(
    users: List<AdminUserDto>,
    state: AdminUsersState,
    launchAction: ((suspend () -> Unit) -> Unit),
) {
    TableScroll {
        Table(attrs = TableStyle.toAttrs()) {
            Thead {
                Tr {
                    Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Usuário") }
                    Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Solicitado em") }
                    Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Perfil") }
                    Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Relatório") }
                    Th(attrs = TableHeaderCellStyle.toAttrs()) { }
                }
            }
            Tbody {
                for (user in users) {
                    Tr(attrs = TableRowHoverStyle.toAttrs()) {
                        Td(attrs = TableCellStyle.toAttrs()) {
                            Row(modifier = Modifier.gap(0.375.cssRem), verticalAlignment = Alignment.CenterVertically) {
                                Text(user.username)
                                user.displayName?.let { name ->
                                    Span(attrs = AdminNameHintStyle.toAttrs { attr("title", name) }) {
                                        Text(name)
                                    }
                                }
                            }
                        }
                        Td(attrs = TableCellStyle.toAttrs()) { Text(user.requestedAt) }
                        Td(attrs = TableCellStyle.toAttrs()) {
                            PresetSelect(
                                user = user,
                                current = state.presetFor(user),
                                onChange = { state.setPreset(user.username, it) },
                            )
                        }
                        Td(attrs = TableCellStyle.toAttrs()) {
                            val preset = state.presetFor(user)
                            Checkbox(
                                checked = preset == AccessPreset.ADMIN || state.reportFor(user),
                                onCheckedChange = { state.setReport(user.username, it) },
                                enabled = preset != AccessPreset.ADMIN,
                                size = CheckboxSize.SM,
                                content = { Text("Liberar") },
                            )
                        }
                        Td(attrs = TableCellStyle.toAttrs()) {
                            Row(modifier = Modifier.gap(0.375.cssRem)) {
                                Button(
                                    onClick = { launchAction { state.activateInline(user) } },
                                    variant = PrimaryButtonVariant,
                                    size = ButtonSize.SM,
                                ) { Text("Ativar") }
                                Button(
                                    onClick = { launchAction { state.openEditor(user, state.pendingDraft(user)) } },
                                    variant = LinkButtonVariant,
                                    size = ButtonSize.SM,
                                ) { Text("Revisar") }
                                Button(
                                    onClick = { state.denyTarget = user },
                                    variant = DangerButtonVariant,
                                    size = ButtonSize.SM,
                                ) { Text("Negar") }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ActiveTable(
    users: List<AdminUserDto>,
    state: AdminUsersState,
    launchAction: ((suspend () -> Unit) -> Unit),
) {
    TableScroll {
        Table(attrs = TableStyle.toAttrs()) {
            Thead {
                Tr {
                    Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Usuário") }
                    Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Último acesso") }
                    Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Permissões") }
                    Th(attrs = TableHeaderCellStyle.toAttrs()) { }
                }
            }
            Tbody {
                for (user in users) {
                    val self = user.username == state.currentUsername
                    Tr(attrs = TableRowHoverStyle.toAttrs()) {
                        Td(attrs = TableCellStyle.toAttrs()) {
                            Row(modifier = Modifier.gap(0.375.cssRem), verticalAlignment = Alignment.CenterVertically) {
                                Text(if (self) "${user.username} (você)" else user.username)
                                user.displayName?.let { name ->
                                    Span(attrs = AdminNameHintStyle.toAttrs { attr("title", name) }) {
                                        Text(name)
                                    }
                                }
                            }
                        }
                        Td(attrs = TableCellStyle.toAttrs()) { Text(user.lastLoginAt ?: "—") }
                        Td(attrs = TableCellStyle.toAttrs()) {
                            val chips = permissionChips(user.grants)
                            // Fixed-width token block: chips wrap onto extra lines instead of widening the table.
                            Row(modifier = Modifier.width(16.cssRem).gap(0.25.cssRem).flexWrap(FlexWrap.Wrap)) {
                                for ((label, tone) in chips.take(3)) AccessChip(label, tone)
                                if (chips.size > 3) AccessChip("+${chips.size - 3}", AccessChipTone.MUTED)
                            }
                        }
                        Td(attrs = TableCellStyle.toAttrs()) {
                            Button(
                                onClick = { launchAction { state.openEditor(user) } },
                                variant = LinkButtonVariant,
                                size = ButtonSize.SM,
                            ) { Text("Editar") }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun DeniedTable(
    users: List<AdminUserDto>,
    state: AdminUsersState,
    launchAction: ((suspend () -> Unit) -> Unit),
) {
    TableScroll {
        Table(attrs = TableStyle.toAttrs()) {
            Thead {
                Tr {
                    Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Usuário") }
                    Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Negado") }
                    Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Permissões suspensas") }
                    Th(attrs = TableHeaderCellStyle.toAttrs()) { }
                }
            }
            Tbody {
                for (user in users) {
                    Tr(attrs = TableRowHoverStyle.toAttrs()) {
                        Td(attrs = TableCellStyle.toAttrs()) { Text(user.username) }
                        Td(attrs = TableCellStyle.toAttrs()) {
                            Text(
                                "negado desde ${user.deniedAt.orEmpty()}" +
                                    (user.deniedBy?.let { " por $it" } ?: "")
                            )
                        }
                        Td(attrs = TableCellStyle.toAttrs()) {
                            val chips = permissionChips(user.grants)
                            // Same fixed-width token block as the active table.
                            Row(modifier = Modifier.width(16.cssRem).gap(0.25.cssRem).flexWrap(FlexWrap.Wrap)) {
                                for ((label, tone) in chips.take(3)) AccessChip(label, tone)
                                if (chips.size > 3) AccessChip("+${chips.size - 3}", AccessChipTone.MUTED)
                            }
                        }
                        Td(attrs = TableCellStyle.toAttrs()) {
                            Button(
                                onClick = { launchAction { state.openEditor(user) } },
                                variant = PrimaryButtonVariant,
                                size = ButtonSize.SM,
                            ) { Text("Revisar") }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SyncPanel(state: AdminUsersState, launchAction: ((suspend () -> Unit) -> Unit)) {
    Panel {
        CollapsiblePanelTitle(
            expanded = state.syncExpanded,
            onToggle = { state.syncExpanded = !state.syncExpanded },
        ) {
            Text("Sincronização de chefias")
            if (state.syncExpanded) {
                ChevronUpIcon(Modifier.margin(left = 0.375.cssRem))
            } else {
                ChevronDownIcon(Modifier.margin(left = 0.375.cssRem))
            }
        }
        if (state.syncExpanded) {
            when {
                !state.syncLoaded -> P(attrs = PanelEmptyStyle.toAttrs()) { Text("Carregando…") }
                state.syncError.isNotEmpty() ->
                    P(attrs = Modifier.color(IndustrialPalette.Red).margin(0.cssRem).toAttrs()) {
                        Text(state.syncError)
                    }
                state.syncRun == null -> P(attrs = PanelEmptyStyle.toAttrs()) {
                    Text("Nenhuma sincronização de chefias executada.")
                }
                else -> {
                    val run = state.syncRun!!
                    Column(modifier = Modifier.padding(0.75.cssRem).gap(0.375.cssRem)) {
                        Text("Execução ${run.id} · ${runStatusLabel(run.status)} · ${run.startedAt.orEmpty()}")
                        Text(
                            "Recebidas ${run.received} · resolvidas ${run.resolved} · " +
                                "removidas ${run.removed} · permissões revogadas ${run.revokedGrants}"
                        )
                        if (run.baseline) {
                            P(attrs = SyncWarningStyle.toAttrs()) {
                                Text("Primeira sincronização: nenhuma permissão foi revogada.")
                            }
                        }
                        run.warning?.let { warning ->
                            P(attrs = SyncWarningStyle.toAttrs()) { Text(warning) }
                        }
                        run.error?.let { error ->
                            P(attrs = Modifier.color(IndustrialPalette.Red).margin(0.cssRem).toAttrs()) {
                                Text(error)
                            }
                        }
                        if (run.removedUsernames.isNotEmpty()) {
                            P(attrs = AdminHintStyle.toAttrs()) {
                                Text("Fora da lista: ${run.removedUsernames.joinToString(", ")}")
                            }
                        }
                        run.revertedAt?.let { revertedAt ->
                            P(attrs = AdminHintStyle.toAttrs()) { Text("Revertida em $revertedAt.") }
                        }
                        if (run.status == "DONE" && run.revertedAt == null) {
                            Button(
                                onClick = { launchAction { state.revertSync() } },
                                variant = DangerButtonVariant,
                                size = ButtonSize.SM,
                            ) { Text("Reverter esta execução") }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun PresetSelect(
    user: AdminUserDto,
    current: AccessPreset,
    onChange: (AccessPreset) -> Unit,
) {
    val selectId = "preset-select-${user.username}"
    LaunchedEffect(current) {
        (document.getElementById(selectId) as? HTMLSelectElement)?.value = current.name
    }
    Select(
        attrs = PresetSelectStyle.toModifier().toAttrs {
            attr("id", selectId)
            attr("aria-label", "Perfil de ${user.username}")
            onChange { event ->
                val target = event.target as? HTMLSelectElement ?: return@onChange
                AccessPreset.entries.firstOrNull { it.name == target.value }?.let(onChange)
            }
        }
    ) {
        for (preset in AccessPreset.entries) {
            Option(
                value = preset.name,
                attrs = { if (current == preset) attr("selected", "selected") },
            ) { Text(presetLabel(preset)) }
        }
    }
}

@Composable
private fun AccessChip(text: String, tone: AccessChipTone) {
    val variant = when (tone) {
        AccessChipTone.NORMAL -> null
        AccessChipTone.WARNING -> AccessChipWarningVariant
        AccessChipTone.DANGER -> AccessChipDangerVariant
        AccessChipTone.MUTED -> AccessChipMutedVariant
    }
    Span(attrs = AccessChipStyle.toModifier(variant).toAttrs()) { Text(text) }
}

// ------------------------------------------------------------------ actions

private suspend fun load(state: AdminUsersState) {
    val meResponse = runCatching { window.http.get("/api/me") }.getOrNull() ?: return
    if (meResponse.status.toInt() == 401 || meResponse.redirected) return redirectToLogin()
    if (!meResponse.ok) return
    val me = runCatching {
        json.decodeFromString<MeDto>(meResponse.bodyAsBytes().decodeToString())
    }.getOrNull() ?: return
    if (!me.canManageAccess) {
        window.location.href = "/"
        return
    }
    state.me = me
    state.currentUsername = me.username
    state.refresh()
}

internal suspend fun AdminUsersState.refresh() {
    val response = runCatching { window.http.get("/api/admin/users") }.getOrNull() ?: return
    if (response.status.toInt() == 401 || response.redirected) return redirectToLogin()
    if (!response.ok) {
        error = errorMessage(response)
        loaded = true
        return
    }
    val body = runCatching {
        json.decodeFromString<AdminUsersDto>(response.bodyAsBytes().decodeToString())
    }.getOrNull() ?: return
    pending = body.pending
    active = body.active
    denied = body.denied
    error = ""
    loaded = true
    loadSync()
}

internal suspend fun AdminUsersState.loadSync() {
    val response = runCatching { window.http.get("/api/admin/leadership/runs/latest") }.getOrNull()
    if (response == null) {
        syncError = "Não foi possível carregar a sincronização."
        syncLoaded = true
        return
    }
    if (response.status.toInt() == 401 || response.redirected) return redirectToLogin()
    if (response.status.toInt() == 404) {
        syncRun = null
        syncError = ""
        syncLoaded = true
        return
    }
    if (!response.ok) {
        syncError = errorMessage(response)
        syncLoaded = true
        return
    }
    syncRun = runCatching {
        json.decodeFromString<LeadershipRunDto>(response.bodyAsBytes().decodeToString())
    }.getOrNull()
    syncError = ""
    syncLoaded = true
}

internal suspend fun AdminUsersState.openEditor(
    user: AdminUserDto,
    initialDraft: Set<AccessGrant>? = null,
) {
    val adminCount = active.count { it.grants.has(Capability.MANAGE_ACCESS) }
    val isLastAdmin = user.status == "ACTIVE" &&
        user.grants.has(Capability.MANAGE_ACCESS) && adminCount <= 1
    val editor = AccessEditorState(
        user = user,
        isLastAdmin = isLastAdmin,
        isSelf = user.username == currentUsername,
    )
    if (initialDraft != null) editor.draft = initialDraft
    this.editor = editor
    val response = runCatching {
        window.http.get("/api/admin/users/${encodeURIComponent(user.username)}/grants")
    }.getOrNull()
    if (response == null) {
        editor.historyError = "Não foi possível carregar o histórico."
        editor.historyLoaded = true
        return
    }
    if (response.status.toInt() == 401 || response.redirected) return redirectToLogin()
    if (!response.ok) {
        editor.historyError = errorMessage(response)
        editor.historyLoaded = true
        return
    }
    editor.history = runCatching {
        json.decodeFromString<GrantHistoryDto>(response.bodyAsBytes().decodeToString())
    }.getOrNull()?.grants.orEmpty()
    editor.historyLoaded = true
}

internal suspend fun AdminUsersState.activateInline(user: AdminUserDto) {
    val preset = presetFor(user)
    val report = preset == AccessPreset.ADMIN || reportFor(user)
    val response = runCatching {
        window.http.post(
            "/api/admin/users/${encodeURIComponent(user.username)}/activate",
            body = bodyOf(
                json.encodeToString(ActivateUserRequest(preset = preset.name, report = report)),
                "application/json",
            ),
        )
    }.getOrNull() ?: return
    if (response.status.toInt() == 401 || response.redirected) return redirectToLogin()
    if (!response.ok) {
        toast(errorMessage(response))
        return
    }
    toast(
        "Acesso de ${user.username} ativado" +
            if (report) " com acesso ao painel." else " sem acesso ao painel."
    )
    refresh()
}

internal suspend fun AdminUsersState.saveEditor() {
    val editor = editor ?: return
    editor.saving = true
    editor.error = ""
    val grants = editor.draft.map { GrantDto(capability = it.capability.name, scope = it.scope.name) }
    val username = encodeURIComponent(editor.user.username)
    val response = runCatching {
        if (editor.user.status == "ACTIVE") {
            window.http.post(
                "/api/admin/users/$username/access",
                body = bodyOf(
                    json.encodeToString(UpdateAccessRequest(grants = grants)),
                    "application/json",
                ),
            )
        } else {
            window.http.post(
                "/api/admin/users/$username/activate",
                body = bodyOf(
                    json.encodeToString(ActivateUserRequest(grants = grants)),
                    "application/json",
                ),
            )
        }
    }.getOrNull()
    editor.saving = false
    if (response == null) {
        editor.error = "Não foi possível salvar. Tente novamente."
        return
    }
    if (response.status.toInt() == 401 || response.redirected) return redirectToLogin()
    if (!response.ok) {
        editor.error = errorMessage(response)
        return
    }
    this.editor = null
    val removingSelfAdmin = editor.user.username == currentUsername &&
        !editor.effective.any { it.capability == Capability.MANAGE_ACCESS }
    if (removingSelfAdmin) {
        window.location.href = if (editor.effective.grants(Capability.VIEW_REPORT)) "/" else "/sem-acesso"
        return
    }
    toast("Permissões de ${editor.user.username} atualizadas.")
    refresh()
}

internal suspend fun AdminUsersState.deny(username: String): Boolean {
    // The caller owns the confirmation: the pending table and the editor both use ConfirmDialog.
    val response = runCatching {
        window.http.post("/api/admin/users/${encodeURIComponent(username)}/deny")
    }.getOrNull() ?: return false
    if (response.status.toInt() == 401 || response.redirected) {
        redirectToLogin()
        return false
    }
    if (!response.ok) {
        toast(errorMessage(response))
        return false
    }
    toast("Acesso de $username negado.")
    refresh()
    return true
}

internal suspend fun AdminUsersState.revertSync() {
    val run = syncRun ?: return
    val confirmed = window.confirm(
        "Reverter a sincronização ${run.id}? As permissões sincronizadas revogadas por ela serão " +
            "restauradas. Se a lista de chefias ainda estiver incorreta, a próxima sincronização " +
            "pode revogá-las novamente."
    )
    if (!confirmed) return
    val response = runCatching {
        window.http.post("/api/admin/leadership/runs/${run.id}/revert")
    }.getOrNull() ?: return
    if (response.status.toInt() == 401 || response.redirected) return redirectToLogin()
    if (!response.ok) {
        toast(errorMessage(response))
        return
    }
    toast("Sincronização ${run.id} revertida.")
    loadSync()
    refresh()
}

private fun redirectToLogin() {
    window.location.href = "/login"
}

private suspend fun errorMessage(response: org.w3c.fetch.Response): String = try {
    json.decodeFromString<ErrorDto>(response.bodyAsBytes(requireOk = false).decodeToString()).error
} catch (t: Throwable) {
    "erro ${response.status}"
}

private external fun encodeURIComponent(value: String): String
